// What a leave request row on the employee's Leaves tab shows, from the
// serialized leave request and its attached document.
import axios from "axios";
import { type RowSigner, toClickRowSigner } from "features/files/file-row-model";
import type { PillTone } from "features/files/file-row-parts";
import type { SerializedLeaveRequest } from "services/leave-requests.service";
import { hasPdfExtension } from "utils/helperFunctions";

/** The leave request's own status word: approved green, waiting amber, refused red. */
export function leaveStatusTone(status: string): PillTone {
  if (status === "approved") return "green";
  if (status === "pending_approval") return "amber";
  if (status === "rejected" || status === "cancelled") return "red";
  return "zinc";
}

/**
 * Everyone on the leave document's signersList. The logged-in user's own
 * pending line can be signed, except on a read-only view (someone else's card).
 */
export function getLeaveDocumentSigners(
  leave: SerializedLeaveRequest,
  currentUserId: string | undefined,
  readOnly: boolean
): RowSigner[] {
  return (leave.file?.signersList || []).map((signer) =>
    toClickRowSigner(signer, currentUserId, !readOnly)
  );
}

/**
 * "09:00–12:00" for an hourly or half-day leave; only the known end when one
 * is missing; "" for a full day or no times.
 */
export function leaveTimeRange(leave: SerializedLeaveRequest): string {
  if (leave.duration !== "hourly" && leave.duration !== "half_day") return "";
  if (leave.startTime && leave.endTime) return `${leave.startTime}–${leave.endTime}`;
  return leave.startTime || leave.endTime || "";
}

/** Shared with the wall calendar (LeaveRecord and SerializedLeaveRequest have the same file shape). */
export function canOpenLeavePdf(row: {
  fileId?: string | null;
  file?: {
    fileId?: string | null;
    title?: string | null;
    name?: string | null;
  } | null;
}): boolean {
  if (!row.fileId || !row.file?.fileId) return false;
  const label = row.file?.title || row.file?.name || "";
  return hasPdfExtension(label) || label.length === 0;
}

/** Stored with the withdrawal and sent to the people who were asked to sign. */
export const OWN_CANCEL_REASON = "Darbuotojas atšaukė prašymą";

/**
 * The person's own request can be taken back while it waits for approval and its
 * document is not signed by everyone, withdrawn or annulled. The server decides the rest
 * (a document that cannot be withdrawn at all answers with an error).
 */
export function canCancelOwnLeave(leave: SerializedLeaveRequest, currentUserId: string | undefined): boolean {
  const file = leave.file;
  return (
    !!currentUserId &&
    leave.userId === currentUserId &&
    leave.status === "pending_approval" &&
    !!leave.fileId &&
    !!file &&
    !file.isSigned &&
    !file.withdrawnAt &&
    !file.annulledAt
  );
}

// Why the server refused to withdraw the document, by the `message` it sent: the
// translation key of the line to show. A reason not listed gets the generic line.
const CANCEL_ERROR_KEYS: Record<string, string> = {
  document_already_signed: "leave_cancel_error_signed",
  document_annulled: "queue_error_annulled",
  document_withdrawn: "document_withdrawn",
  withdraw_not_template: "withdraw_not_template",
  withdraw_not_supported: "withdraw_not_supported",
  withdraw_forbidden: "withdraw_forbidden",
};

export function cancelErrorKey(error: unknown): string {
  const message = axios.isAxiosError(error) ? error.response?.data?.message : undefined;
  return CANCEL_ERROR_KEYS[message] ?? "leave_cancel_error_generic";
}
